// services/combinedSchedule.ts
import { scheduleService } from './schedule';
import { otherScheduleService } from './otherSchedule';
import { CombinedScheduleItem, convertToCommonFormat } from '../types/commonSchedule';
import { Schedule } from '../types/schedule';
import { OtherSchedule } from '../types/otherSchedule';

export const combinedScheduleService = {
  // 両方のスケジュールを取得して統合する
  async getCombinedSchedules(startDate: string, endDate: string): Promise<CombinedScheduleItem[]> {
    try {
      // 工事予定を取得
      const constructionData = await scheduleService.getSchedulesByDateRange(startDate, endDate);
      const constructionSchedules = constructionData && constructionData.schedules && Array.isArray(constructionData.schedules)
        ? constructionData.schedules.map((schedule: Schedule) => 
            convertToCommonFormat(schedule, 'construction')
          )
        : [];
      
      // その他予定を取得
      const otherData = await otherScheduleService.getOtherSchedulesByDateRange(startDate, endDate);
      const otherSchedules = otherData && otherData.schedules && Array.isArray(otherData.schedules)
        ? otherData.schedules.map((schedule: OtherSchedule) => 
            convertToCommonFormat(schedule, 'other')
          )
        : [];
      
      // 全スケジュールを日付と時間でソート
      const allSchedules = [...constructionSchedules, ...otherSchedules].sort((a, b) => {
        // 日付でソート
        if (!a.scheduleDate || !b.scheduleDate) return 0;
        
        const dateCompare = new Date(a.scheduleDate).getTime() - new Date(b.scheduleDate).getTime();
        if (dateCompare !== 0) return dateCompare;
        
        // 同じ日付の場合は時間でソート
        if (a.startTime && b.startTime) {
          return a.startTime.localeCompare(b.startTime);
        }
        
        // 終日予定を後ろに配置
        if (a.isAllDay && !b.isAllDay) return 1;
        if (!a.isAllDay && b.isAllDay) return -1;
        
        return 0;
      });
      
      return allSchedules;
    } catch (error) {
      console.error('スケジュール取得中にエラーが発生しました:', error);
      return [];
    }
  },
  
  // スケジュールの詳細を取得
  async getScheduleDetail(id: number, type: 'construction' | 'other'): Promise<any> {
    if (type === 'construction') {
      return scheduleService.getScheduleById(id);
    } else {
      return otherScheduleService.getOtherScheduleById(id);
    }
  },
  
  // スケジュールの作成
  async createSchedule(schedule: any, type: 'construction' | 'other'): Promise<any> {
    if (type === 'construction') {
      return scheduleService.createSchedule(schedule as Schedule);
    } else {
      return otherScheduleService.createOtherSchedule(schedule as OtherSchedule);
    }
  },
  
  // スケジュールの更新
  async updateSchedule(id: number, schedule: any, type: 'construction' | 'other'): Promise<any> {
    if (type === 'construction') {
      return scheduleService.updateSchedule(id, schedule as Partial<Schedule>);
    } else {
      return otherScheduleService.updateOtherSchedule(id, schedule as Partial<OtherSchedule>);
    }
  },
  
  // スケジュールの削除
  async deleteSchedule(id: number, type: 'construction' | 'other'): Promise<any> {
    if (type === 'construction') {
      return scheduleService.deleteSchedule(id);
    } else {
      return otherScheduleService.deleteOtherSchedule(id);
    }
  },
  
  // マスターデータの取得（共通関数）
  async getMembers() {
    return scheduleService.getMembers();
  },
  
  async getVendorMembers() {
    return scheduleService.getVendorMembers();
  },
  
  async getCars() {
    return scheduleService.getCars();
  },
  
  async getCustomers() {
    return otherScheduleService.getCustomers();
  },
  
  async getConstructions() {
    return scheduleService.getConstructions();
  }
};